/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  QueryFilter,
  QueryFilter$Outbound,
  QueryFilter$outboundSchema,
} from "./queryfilter.js";

/**
 * Optional dimension to break results down by. When omitted, a single aggregate row/series for the whole slice is returned.
 */
export const GroupBy = {
  DepartmentName: "department_name",
  JobTitle: "job_title",
  EmployeeType: "employee_type",
  DivisionName: "division_name",
  CostCenterName: "cost_center_name",
  Email: "email",
  Hostname: "hostname",
  Model: "model",
  HookSource: "hook_source",
  AccountType: "account_type",
  Provider: "provider",
  BillingMode: "billing_mode",
  QuerySource: "query_source",
  SkillName: "skill_name",
  SkillVersion: "skill_version",
  AgentName: "agent_name",
  McpServerName: "mcp_server_name",
  McpToolName: "mcp_tool_name",
  Role: "role",
  Group: "group",
  ProjectId: "project_id",
} as const;
/**
 * Optional dimension to break results down by. When omitted, a single aggregate row/series for the whole slice is returned.
 */
export type GroupBy = ClosedEnum<typeof GroupBy>;

/**
 * Measure used to rank groups for top_n. Defaults to total_cost.
 */
export const QueryPayloadSortBy = {
  TotalCost: "total_cost",
  TotalTokens: "total_tokens",
  LlmTokens: "llm_tokens",
  TotalInputTokens: "total_input_tokens",
  TotalOutputTokens: "total_output_tokens",
  CacheReadInputTokens: "cache_read_input_tokens",
  CacheCreationInputTokens: "cache_creation_input_tokens",
  TotalToolCalls: "total_tool_calls",
  TotalChats: "total_chats",
  TotalWorkUnits: "total_work_units",
  ScoredCost: "scored_cost",
  ScoredTokens: "scored_tokens",
} as const;
/**
 * Measure used to rank groups for top_n. Defaults to total_cost.
 */
export type QueryPayloadSortBy = ClosedEnum<typeof QueryPayloadSortBy>;

/**
 * Payload for a generic org-scoped analytics query. Queries involving skill_version use raw telemetry retained for 90 days and attribute each whole session to every activated version; timeseries place the whole session in its start-time bucket.
 */
export type QueryPayload = {
  /**
   * Optional filters; all filters are ANDed together.
   */
  filters?: Array<QueryFilter> | undefined;
  /**
   * Start time in ISO 8601 format
   */
  from: Date;
  /**
   * Optional timeseries bucket size in seconds. Defaults to an interval derived from the time range and is floored to 3600 (the source data is bucketed hourly).
   */
  granularitySeconds?: number | undefined;
  /**
   * Optional dimension to break results down by. When omitted, a single aggregate row/series for the whole slice is returned.
   */
  groupBy?: GroupBy | undefined;
  /**
   * Whether to include distinct values for other dimensions in each table row. When omitted, defaults to true.
   */
  includeDimensionValues?: boolean | undefined;
  /**
   * Measure used to rank groups for top_n. Defaults to total_cost.
   */
  sortBy?: QueryPayloadSortBy | undefined;
  /**
   * End time in ISO 8601 format
   */
  to: Date;
  /**
   * When group_by is set, keep at most this many groups (ranked by sort_by); the remainder are rolled into an 'Other' group. Defaults to 10.
   */
  topN?: number | undefined;
};

/** @internal */
export const GroupBy$outboundSchema: z.ZodMiniEnum<typeof GroupBy> = z.enum(
  GroupBy,
);

/** @internal */
export const QueryPayloadSortBy$outboundSchema: z.ZodMiniEnum<
  typeof QueryPayloadSortBy
> = z.enum(QueryPayloadSortBy);

/** @internal */
export type QueryPayload$Outbound = {
  filters?: Array<QueryFilter$Outbound> | undefined;
  from: string;
  granularity_seconds?: number | undefined;
  group_by?: string | undefined;
  include_dimension_values: boolean;
  sort_by: string;
  to: string;
  top_n: number;
};

/** @internal */
export const QueryPayload$outboundSchema: z.ZodMiniType<
  QueryPayload$Outbound,
  QueryPayload
> = z.pipe(
  z.object({
    filters: z.optional(z.array(QueryFilter$outboundSchema)),
    from: z.pipe(z.date(), z.transform(v => v.toISOString())),
    granularitySeconds: z.optional(z.int()),
    groupBy: z.optional(GroupBy$outboundSchema),
    includeDimensionValues: z._default(z.boolean(), true),
    sortBy: z._default(QueryPayloadSortBy$outboundSchema, "total_cost"),
    to: z.pipe(z.date(), z.transform(v => v.toISOString())),
    topN: z._default(z.int(), 10),
  }),
  z.transform((v) => {
    return remap$(v, {
      granularitySeconds: "granularity_seconds",
      groupBy: "group_by",
      includeDimensionValues: "include_dimension_values",
      sortBy: "sort_by",
      topN: "top_n",
    });
  }),
);

export function queryPayloadToJSON(queryPayload: QueryPayload): string {
  return JSON.stringify(QueryPayload$outboundSchema.parse(queryPayload));
}
